import {
  Component, Input, Output, EventEmitter,
  ViewChild, ElementRef, OnDestroy, AfterViewInit, OnChanges, SimpleChanges, HostListener
} from '@angular/core';
import Hls from 'hls.js';

@Component({
    selector: 'app-hls-overlay-player',
    templateUrl: './inspark-overlay-hls-video.component.html',
    styleUrls: ['./inspark-overlay-hls-video.component.scss'],
    standalone: false
})
export class InsparkOverlayHlsVideoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() streamUrl!: string;            // например: 'http://10.3.2.103/media-gate/hls/8569/stream.m3u8'
  @Input() autoPlayMuted: boolean = true; // для автоплея в браузерах
  @Output() closed = new EventEmitter<void>();

  @ViewChild('videoEl') videoRef!: ElementRef<HTMLVideoElement>;
  private hls?: Hls;
  private nativeMediaCleanup?: () => void;
  isLoading = true;
  errorMsg: string | null = null;

  ngAfterViewInit(): void {
    if (this.streamUrl) {
      this.initPlayer(this.streamUrl);
    }
    // Заблокируем прокрутку body, пока открыт оверлей
    document.body.style.overflow = 'hidden';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['streamUrl'] && !changes['streamUrl'].firstChange && this.videoRef) {
      this.reinit(changes['streamUrl'].currentValue);
    }
  }

  private reinit(url: string) {
    this.destroyPlayer();
    this.isLoading = true;
    this.errorMsg = null;
    this.initPlayer(url);
  }

  private initPlayer(url: string) {
    const video = this.videoRef.nativeElement;
    video.muted = this.autoPlayMuted;
    video.playsInline = true;

    // Проверяем расширение без query/hash (в том числе для подписанных ссылок).
    const isMp4 = /\.mp4$/i.test(url.split(/[?#]/, 1)[0]);
    if (isMp4) {
      this.initNativePlayer(video, url);
    } else if (Hls.isSupported()) {
      this.hls = new Hls({
        // Опционально: тонкая настройка буфера/задержки
        enableWorker: true,
        lowLatencyMode: true
      });
      this.hls.loadSource(url);
      this.hls.attachMedia(video);

      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.isLoading = false;
        video.play().catch(() => {
          // Если автоплей запрещен — просто показываем контролы
          this.errorMsg = null;
        });
      });

      this.hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              this.errorMsg = 'Сетевая ошибка. Переподключение...';
              this.hls?.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              this.errorMsg = 'Медиа ошибка. Восстановление...';
              this.hls?.recoverMediaError();
              break;
            default:
              this.errorMsg = 'Критическая ошибка плеера';
              this.close();
          }
        }
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari (нативная поддержка HLS)
      this.initNativePlayer(video, url);
    } else {
      this.isLoading = false;
      this.errorMsg = 'Браузер не поддерживает HLS';
    }
  }

  private initNativePlayer(video: HTMLVideoElement, url: string): void {
    const onLoadedMetadata = () => {
      this.isLoading = false;
      this.errorMsg = null;
      video.play().catch(() => {});
    };
    const onError = () => {
      this.isLoading = false;
      this.errorMsg = 'Не удалось загрузить видео. Проверьте ссылку и формат файла.';
    };
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('error', onError);
    this.nativeMediaCleanup = () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('error', onError);
    };
    video.src = url;
    video.load();
  }

  togglePlay() {
    const video = this.videoRef.nativeElement;
    if (video.paused) {video.play();}
    else {video.pause();}
  }

  close() {
    this.destroyPlayer();
    document.body.style.overflow = ''; // вернуть прокрутку
    this.closed.emit();
  }

  private destroyPlayer() {
    this.nativeMediaCleanup?.();
    this.nativeMediaCleanup = undefined;
    try {
      const video = this.videoRef?.nativeElement;
      video?.pause();
      if (this.hls) {
        this.hls.destroy();
        this.hls = undefined;
      }
      if (video) {
        video.removeAttribute('src');
        video.load();
      }
    } catch {}
  }

  @HostListener('document:keydown.escape')
  onEsc() {
    this.close();
  }

  ngOnDestroy(): void {
    this.destroyPlayer();
    document.body.style.overflow = '';
  }
}
